import React, { useState, useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Checkbox } from "@mui/material";
import { Button, CreateTableNew as CreateTable, SearchBar } from "../../../../../common/APICommon";
import { usePayrollPeriods } from "../../../../hooks/usePayrollPeriods";
import { Select } from "../../../../../common/Select";
import ClassificationPickerField from "../../../../../common/selectComponents/ClassificationPicker";
import { useLeaveEncashment } from "../../../../hooks/useLeaveEncashment"; // Use the hook

const LeaveEncashmentProcessModal = ({ user, isOpen, onClose, onConfirm, currentYear, showGeneralMessage }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [classificationId, setClassificationId] = useState(0);
  const [classificationDescription, setClassificationDescription] = useState("");
  const [targetProcess, setTargetProcess] = useState("payroll");

  const [previewData, setPreviewData] = useState([]);
  const [internalSelectedIds, setInternalSelectedIds] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { periods, loadingPeriods } = usePayrollPeriods(currentYear);

  // MODIFIED: Access the hook's new function
  const { getPreviewData } = useLeaveEncashment({ user, currentYear, showGeneralMessage });

  useEffect(() => {
    const fetchPreview = async () => {
      if (!classificationId) {
        setPreviewData([]);
        setInternalSelectedIds([]);
        return;
      }

      setInternalLoading(true);

      const filterPayload = [
        { type: 3, value: parseInt(classificationId) }
      ];

      const employees = await getPreviewData(filterPayload, selectedPeriodId);

      setPreviewData(employees);
      setInternalSelectedIds([]);
      setInternalLoading(false);
    };

    fetchPreview();
  }, [classificationId]);

  const displayedData = useMemo(() => {
    if (!searchTerm) return previewData;
    const lower = searchTerm.toLowerCase();
    return previewData.filter(emp =>
      emp.fullName?.toLowerCase().includes(lower) ||
      emp.employeeId?.toString().toLowerCase().includes(lower)
    );
  }, [previewData, searchTerm]);

  const toggleAll = (e) => {
    if (e.target.checked) {
      setInternalSelectedIds(displayedData.map(emp => emp.id));
    } else {
      setInternalSelectedIds([]);
    }
  };

  const toggleOne = (id) => {
    setInternalSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleClose = () => {
    onClose();
    setClassificationId(null);
    setSelectedPeriodId(null)
  }

  const headers = useMemo(() => {
    const baseHeaders = ["ID", "Full Name", "Encashable Days", "Total Value"];
    if (classificationId) {
      return [
        <Checkbox
          key="select-all-cb"
          size="small"
          onChange={toggleAll}
          checked={displayedData.length > 0 && internalSelectedIds.length === displayedData.length}
        />,
        ...baseHeaders
      ];
    }
    return baseHeaders;
  }, [classificationId, displayedData, internalSelectedIds]);

  const accessors = useMemo(() => {
    const baseAccessors = ["employeeId", "fullName", "totalEncashable", "totalGross"];
    if (classificationId) {
      return ["selection", ...baseAccessors];
    }
    return baseAccessors;
  }, [classificationId]);

  const customRender = (row, accessor) => {
    if (accessor === "selection") return (
      <Checkbox checked={internalSelectedIds.includes(row.id)} onChange={() => toggleOne(row.id)} size="small" />
    );
    if (accessor === "totalEncashable") return <span className="font-bold text-blue-700">{row[accessor]} Days</span>;
    if (accessor === "totalGross") return <span className="font-extrabold text-blue-900">₱{row[accessor].toLocaleString()}</span>;
    return null;
  };

  if (!isOpen) return null;

  const grandTotalValue = previewData
    .filter(emp => internalSelectedIds.includes(emp.id))
    .reduce((sum, item) => sum + parseFloat(item.totalGross || 0), 0);


  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto" style={{ marginTop: 0 }}>
      <div className="bg-white w-full md:w-[90%] lg:w-[75%] rounded-lg shadow-2xl h-auto max-h-[95vh] flex flex-col relative">

        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-blue-900 tracking-tight">Process Leave Encashment</h2>
            <p className="text-xs text-gray-500 uppercase font-semibold">Step 1: Configure Destination & Filter Classification</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-700 bg-transparent border-none cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-grow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm font-bold text-blue-800 uppercase block mb-3">1. Select Destination Staging</span>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-gray-700">
                  <input type="radio" name="targetProcess" value="payroll" checked={targetProcess === "payroll"} onChange={(e) => setTargetProcess(e.target.value)} />
                  Regular Payroll
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-gray-700">
                  <input type="radio" name="targetProcess" value="finalpay" checked={targetProcess === "finalpay"} onChange={(e) => setTargetProcess(e.target.value)} />
                  Final Pay
                </label>
              </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <span className="text-sm font-bold text-amber-800 uppercase block">2. Target Period & Classification</span>
              <Select
                placeholder={loadingPeriods ? "Loading periods..." : "Select Payroll Period..."}
                options={periods}
                value={selectedPeriodId}
                onChange={(e) => setSelectedPeriodId(e.target.value)}
                isDisabled={loadingPeriods}
              />
              <ClassificationPickerField
                label="Filter Classification"
                classificationId={classificationId || ""}
                displayValue={classificationDescription || "-- Select Classification to Load Employees --"}
                onSelect={(classification) => {
                  setClassificationId(classification.id);
                  setClassificationDescription(classification.description);
                }}
                onClear={() => {
                  setClassificationId(0);
                  setClassificationDescription("");
                }}
                disabled={!selectedPeriodId}
                allowClear={true}
                allowAll={true}
                isRequired={true}
              />
            </div>
          </div>

          <div className="mb-4">
            <SearchBar handleFilter={setSearchTerm} placeholder="Search names or IDs within classification..." />
          </div>

          <div className="relative min-h-[300px]">
            {internalLoading && (
              // MODIFIED: Use the newly defined animate-fadeIn
              <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center font-bold animate-fadeIn">
                <div className="flex flex-col items-center gap-2">
                  <span className="text-blue-600">Loading Preview...</span>
                </div>
              </div>
            )}

            {displayedData.length === 0 && !internalLoading ? (
              <div
                key={`empty-${classificationId}`}
                className="text-center py-10 text-gray-400 italic border border-dashed rounded-lg animate-fadeIn"
              >
                {classificationId ? "No employees with encashable balances found for this classification." : "Select a Classification above to load employees."}
              </div>
            ) : (
              // MODIFIED: Use the newly defined animate-fadeIn
              <div key={`table-${classificationId}`} className="animate-fadeIn">
                <CreateTable
                  tableName="SyncProcessTable"
                  headers={headers}
                  accessors={accessors}
                  data={displayedData}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={8}
                  totalItems={displayedData.length}
                  customCellRenderer={customRender}
                />
              </div>
            )}
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg flex justify-between items-center">
            <div className="text-sm">
              <span className="text-gray-600">Selected for Sync: </span>
              <span className="font-bold text-blue-900">{internalSelectedIds.length} Employees</span>
            </div>
            <div className="text-lg">
              <span className="text-gray-600 font-medium">Total Amount: </span>
              <span className="font-black text-blue-900">₱{grandTotalValue.toLocaleString()}.00</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
          <button onClick={handleClose} className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded-lg cursor-pointer border-none transition-colors font-bold">
            Cancel
          </button>
          <button
            disabled={internalLoading || internalSelectedIds.length === 0 || !selectedPeriodId}
            onClick={() => onConfirm(selectedPeriodId, classificationId, targetProcess, internalSelectedIds)}
            className="bg-green-600 hover:bg-green-700 text-white px-10 py-2 rounded-lg cursor-pointer border-none transition-colors font-bold shadow-md disabled:opacity-50"
          >
            Proceed to Confirmation
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaveEncashmentProcessModal;